// Centralized logging utility that respects environment
const isDevelopment = import.meta.env.DEV;
const isTest = import.meta.env.MODE === "test";
// const isProduction = import.meta.env.PROD; // not used

// Logging state
let loggingEnabled: boolean | null = null;
let loggingConfigPromise: Promise<boolean> | null = null;

// Fetch logging configuration from backend
const fetchLoggingConfig = async (): Promise<boolean> => {
  // In test environment, always enable logging
  if (isTest) {
    return true;
  }

  // In production, check backend setting
  // In development, also check backend setting to allow developers to toggle logs
  try {
    const { API_BASE_URL } = await import("../services/common/constants");
    const response = await fetch(`${API_BASE_URL}/api/config/logging`);

    if (response.ok) {
      const data = await response.json();
      return data.enableConsoleLogs === true;
    }

    // If API fails, fall back to environment-based logic
    return isDevelopment;
  } catch {
    // If fetch fails, fall back to environment-based logic
    return isDevelopment;
  }
};

// Initialize logging configuration
const getLoggingEnabled = async (): Promise<boolean> => {
  // Return cached value if available
  if (loggingEnabled !== null) {
    return loggingEnabled;
  }

  // If already fetching, wait for that promise
  if (loggingConfigPromise) {
    return loggingConfigPromise;
  }

  // Start fetching config
  loggingConfigPromise = fetchLoggingConfig();
  loggingEnabled = await loggingConfigPromise;
  loggingConfigPromise = null;

  return loggingEnabled;
};

// Conditional logging functions
const conditionalLog = async (...args: unknown[]) => {
  const enabled = await getLoggingEnabled();
  if (enabled) {
    console.log(...args);
  }
};

const conditionalError = async (...args: unknown[]) => {
  const enabled = await getLoggingEnabled();
  if (enabled) {
    console.error(...args);
  }
};

const conditionalWarn = async (...args: unknown[]) => {
  const enabled = await getLoggingEnabled();
  if (enabled) {
    console.warn(...args);
  }
};

const conditionalInfo = async (...args: unknown[]) => {
  const enabled = await getLoggingEnabled();
  if (enabled) {
    console.info(...args);
  }
};

const conditionalDebug = async (...args: unknown[]) => {
  const enabled = await getLoggingEnabled();
  if (enabled) {
    console.debug(...args);
  }
};

// Export logger with conditional methods
export const logger = {
  log: conditionalLog,
  error: conditionalError,
  warn: conditionalWarn,
  info: conditionalInfo,
  debug: conditionalDebug,
};

// For critical errors that should always be logged (but sanitized)
export const logCriticalError = (message: string, error?: unknown) => {
  // In production, you might want to send this to a logging service
  // For now, we'll just log a sanitized version
  const errorObj = error as Error | null | undefined;
  const sanitizedError = {
    message: errorObj?.message || "Unknown error",
    // Don't log stack traces or sensitive data in production
    ...(isDevelopment && { stack: errorObj?.stack }),
  };

  console.error(`[CRITICAL] ${message}`, sanitizedError);
};
